import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Tooltip, Box } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useUpdateUserMembershipBulkMutation, useUpdateUserOrgStatusMutation } from "state/rtkQuery/user/userMutations";
import { useGetUsersByOrgAndStatusQuery } from "state/rtkQuery/user/userQueries";
import { EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_OWNER_VIEW } from "utils/constants";
import { useDebounce } from "utils/useDebounce";

import AvatarRandomColor from "../ReusableDataGrid/AvatarRandomColor";
import ReusableActions from "../ReusableDataGrid/ReusableActions";
import ReusableDataGrid from "../ReusableDataGrid/ReusableDataGrid";
import { DEFAULT_PAGE_SIZE } from "components/shared/ExpandableGrid/PageSizeSelect";
import { FilterChips, FilterPanel } from "components/shared/FilterPanel/FilterPanel";
import { useFilterPanel } from "components/shared/FilterPanel/useFilterPanel";
import { useTabSortFilter } from "../hooks/useTabSortFilter";
import { useUserCapabilities } from "components/RequirePermission/useCapabilities";

import "../TabStyles.css";

const RemovedTab = ({ orgName }) => {
  const [columns, setColumns] = useState([]);
  const [updatedRows, setUpdatedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
  const { orgId } = useParams();
  const { data: platformData, isFetching: isPlatformDataLoading } =
    useGetPlatformsQuery();

  // Restoring a removed user is the same "update org membership" action as
  // JoinedTab's Actions column — gated on user:id:update, hidden (not
  // disabled) entirely for someone who can see the Removed tab (via
  // organization:read on SideNav) but can't act on it. useUserCapabilities
  // is already scoped to THIS org's role (see useEffectiveOrgPermission).
  const { canUpdateUser } = useUserCapabilities(orgId);

  // Filter options for this tab
  // The filters offered on this tab — all switched-on filters apply together (the API ANDs them).
  const filterFields = useMemo(
    () => [
      { key: "email", label: "Email", type: "text" },
      { key: "first_name", label: "First Name", type: "text" },
      { key: "last_name", label: "Last Name", type: "text" },
      { key: "changed_date", label: "Date of Removal", type: "date" },
      { key: "changed_by", label: "Removed By", type: "text" },
    ],
    []
  );
  const { filters, setFilters, query: filterQuery } = useFilterPanel(filterFields, () => setCurrentPage(1));

  // Use the sorting and filtering hook
  const {
    sortBy,
    sortOrder,
    nameSortMenuAnchor,
    handleNameSortMenuOpen,
    renderNameSortMenu,
    renderNameSortIcon,
    renderSortIcon,
  } = useTabSortFilter([], {}, setCurrentPage);

  const {
    data: removedUserListData,
    isFetching: isRemovedUserListDataLoading,
  } = useGetUsersByOrgAndStatusQuery(
    {
      orgId: orgId,
      status: "Removed",
      platformId: platformData?._id,
      page: currentPage,
      perPage,
      sortBy: sortBy || undefined,
      sortOrder: sortOrder || undefined,
      extraFilters: filterQuery,
    },
    { skip: !platformData?._id, refetchOnMountOrArgChange: true }
  );

  // Set datagrid rows
  useEffect(() => {
    if (removedUserListData) {
      setUpdatedRows(removedUserListData?.users);
    }
  }, [removedUserListData]);

  // Mutation to update user status
  // Bulk mutation
  const [updateUserMembershipBulk, { isLoading: isUpdateUserMembershipBulkLoading }] =
    useUpdateUserMembershipBulkMutation();

  const userEmail = useSelector((state) => state.auth.userAccount?.username);

  // Bulk handler
  const handleBulkAction = async (userIds, rejectReason, otherReason) => {
    try {
      const bulkPayload = {
        platforms: {
          [platformData?._id]: {
            organizations: {
              [orgId]: {
                membership_status: "Not-Requested",
                reason: rejectReason === "Other" ? otherReason : rejectReason,
                changed_by: userEmail?.toLowerCase(),
              },
            },
          },
        },
        user_ids: userIds,
      };
      await updateUserMembershipBulk({
        body: bulkPayload,
        invalidateStatuses: ["Removed"],
      });
    } catch (error) {
      // Handle error
    }
  };

  // Single user action with status check
  // Single user action with status check - use single-user mutation when acting on one user
  const [updateUserOrgStatus] = useUpdateUserOrgStatusMutation();

  const singleUserAction = (params) => async (rejectReason, otherReason) => {
    if (params.row.status === "removed") {
      try {
        const singlePayload = {
          platforms: {
            [platformData?._id]: {
              organizations: {
                [orgId]: {
                  membership_status: "Not-Requested",
                  reason: rejectReason === "Other" ? otherReason : rejectReason,
                  changed_by: userEmail?.toLowerCase(),
                },
              },
            },
          },
        };

        await updateUserOrgStatus({
          userId: params.row.id,
          body: singlePayload,
          attribute: "membership_status",
        }).unwrap();
      } catch (err) {
        // fallback to bulk handler
        handleBulkAction([params.row.id], rejectReason, otherReason);
      }
    }
  };

  // Multi user action
  const multiUserAction = (userIds, rejectReason, otherReason) => {
    handleBulkAction(userIds, rejectReason, otherReason);
  };

  useEffect(() => {
    if (updatedRows) {
      setColumns([
        {
          field: "name",
          headerName: `Showing ${(currentPage - 1) * perPage + 1} - ${(currentPage - 1) * perPage + updatedRows?.length
            } of ${removedUserListData?.total || 0} Users`,
          headerClassName: "history-tab-totalCount-header",
          headerAlign: "left",
          align: "left",
          flex: 2,
          minWidth: 270,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className="history-tab-totalCount-header"
                style={{ 
                  overflow: "hidden", 
                  textOverflow: "ellipsis", 
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0
                }}
              >
                {`Showing ${(currentPage - 1) * perPage + 1} - ${(currentPage - 1) * perPage + updatedRows?.length} of ${removedUserListData?.total || 0} Users`}
              </span>
              {renderNameSortIcon()}
              {renderNameSortMenu(setCurrentPage)}
            </Box>
          ),
          renderCell: (params) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
              }}
            >
              <AvatarRandomColor
                firstName={params.row.firstName}
                lastName={params.row.lastName}
                color={params.row.color}
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginLeft: "20px",
                }}
              >
                <Tooltip
                  title={
                    <span style={{ fontSize: "12px" }}>{params.row.name}</span>
                  }
                  placement="top-start"
                  arrow
                >
                  <span
                    className="table-history-userEmailJoined-cell"
                    style={{ fontWeight: "600", fontSize: "14px" }}
                  >
                    {params.row.name}
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    <span style={{ fontSize: "12px" }}>{params.row.email}</span>
                  }
                  arrow
                  placement="bottom-start"
                >
                  {params.row.email.length >
                    EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_OWNER_VIEW ? (
                    <div>
                      <span
                        className="table-cell-trucate-reason"
                        style={{ paddingLeft: "0", fontSize: "13px" }}
                      >
                        {params.row.email?.slice(
                          0,
                          EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_OWNER_VIEW
                        ) + "..."}
                      </span>{" "}
                    </div>
                  ) : (
                    <span style={{ fontSize: "13px" }}>{params.row.email}</span>
                  )}
                </Tooltip>
              </div>
            </div>
          ),
        },
        {
          field: "DOJ",
          headerName: "Date of Removal",
          headerClassName: "date-rejected-table-header",
          headerAlign: "center",
          align: "center",
          flex: 1,
          minWidth: 140,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className="date-rejected-table-header"
                style={{ 
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: "0 1 auto"
                }}
              >
                Date of Removal
              </span>
              {renderSortIcon("changed_date", "Date of Removal")}
            </Box>
          ),
          valueGetter: (params) => {
            // Return the date as a Date object for proper sorting
            return new Date(params.row.date);
          },
          renderCell: (params) => (
            <span className="table-history-DateRejected-cell">
              {params.row.date}
            </span>
          ),
        },
        {
          field: "removedBy",
          headerName: "Removed By",
          headerClassName: "orgName-column-header",
          headerAlign: "center",
          align: "left",
          flex: 2,
          minWidth: 280,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className="orgName-column-header"
                style={{ 
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: "0 1 auto"
                }}
              >
                Removed By
              </span>
              {renderSortIcon("changed_by", "Removed By")}
            </Box>
          ),
          renderCell: (params) => (
            <Tooltip
              title={<p style={{ fontSize: "12px" }}>{params.row.changedBy}</p>}
            >
              <span className="table-cell-history">{params.row.changedBy}</span>
            </Tooltip>
          ),
        },
        canUpdateUser && {
          field: "action",
          headerAlign: "center",
          headerName: "Actions",
          align: "center",
          flex: 1.5,
          minWidth: 220,
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            <ReusableActions
              params={params}
              action={singleUserAction(params)}
            />
          ),
        },
      ].filter(Boolean));
    }
  }, [updatedRows, currentPage, perPage, removedUserListData?.total, sortBy, sortOrder, nameSortMenuAnchor, canUpdateUser]);

  return (
    <div>
      <ReusableDataGrid
        columns={columns}
        rows={updatedRows}
        orgName={orgName || "Deloitte UK Innersource"}
        viewType={"Removed History"}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        recordsPerPage={perPage}
        onRecordsPerPageChange={(size) => {
          setPerPage(size);
          setCurrentPage(1);
        }}
        checkboxSelection={canUpdateUser}
        totalRecords={removedUserListData?.total || 0}
        isLoading={
          isRemovedUserListDataLoading ||
          isPlatformDataLoading ||
          isUpdateUserMembershipBulkLoading
        }
        multiUserAction={multiUserAction}
        customSearchControls={<FilterPanel fields={filterFields} filters={filters} onFiltersChange={setFilters} height={31} />}
        customSearchInput={<></>}
        filterChips={<FilterChips fields={filterFields} filters={filters} onFiltersChange={setFilters} />}
        tabDescription={
          "Users in Removed have had their access revoked and have been successfully removed from the organization. These users will not be able to re-request access to the organization until their profile has been reset"
        }
      />
    </div>
  );
};

export default RemovedTab;
